<script setup lang="ts">
import type { ScaleLinear } from 'd3';
import type { ScatterChartData, ScatterChartOptions } from '../index.vue';

// ================================================ Props
const props = defineProps<{
  loading: boolean;
  data: ScatterChartData;
  xScale: ScaleLinear<number, number>;
  yScale: ScaleLinear<number, number>;
  options: ScatterChartOptions;
}>();

// ================================================ Computed
const linearRegression = computed(() => {
  const points = props.data.points;
  if (points.length < 2) return null;

  const n = points.length;
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;

  // Calcul des sommes pour la régression linéaire
  points.forEach((point) => {
    sumX += point.x;
    sumY += point.y;
    sumXY += point.x * point.y;
    sumXX += point.x * point.x;
  });

  // Calcul de la pente (slope) et de l'ordonnée à l'origine (intercept)
  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  return { slope, intercept };
});

const linePoints = computed(() => {
  if (!linearRegression.value) return null;

  const { slope, intercept } = linearRegression.value;
  const xDomain = props.xScale.domain();
  const xMin = xDomain[0] ?? 0;
  const xMax = xDomain[1] ?? 10;

  // Calculer les points Y pour les extrémités X
  const yStart = slope * xMin + intercept;
  const yEnd = slope * xMax + intercept;

  // Convertir en pourcentages pour le positionnement
  return {
    x1: props.xScale(xMin),
    y1: props.yScale(yStart),
    x2: props.xScale(xMax),
    y2: props.yScale(yEnd),
  };
});
</script>

<template>
  <div v-if="!loading && linePoints" class="absolute inset-0 w-full h-full pointer-events-none">
    <svg class="absolute inset-0 w-full h-full overflow-visible">
      <line
        :x1="`${linePoints.x1}%`"
        :y1="`${linePoints.y1}%`"
        :x2="`${linePoints.x2}%`"
        :y2="`${linePoints.y2}%`"
        :stroke="options.trendLineColor || '#22c55e'"
        stroke-width="2"
        stroke-opacity="0.8"
      />
    </svg>
  </div>
</template>
